import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLeonLoading } from '../components/LeonLoadingSystem';
import { 
  Upload, 
  FileSearch, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  File as FileIcon,
  Image,
  Camera,
  Building2,
  MapPin,
  FileCheck,
  Shield,
  CreditCard,
  Wrench,
  ListChecks,
  ClipboardCheck,
  CircleDollarSign,
  Info,
  Mail,
  FileText,
  Sparkles,
  Gauge,
  Copy,
  X,
  RefreshCw,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LeonAvatar from '../components/LeonAvatar';
import MobilePhotoUpload from '../components/MobilePhotoUpload';
import { LeonTutorial, TutorialWelcomeCard, useTutorial } from '../components/LeonTutorial';
import EnrichedQuoteReport from '../components/EnrichedQuoteReport';
import { useAdGate, BannerAd } from '../components/AdSystem';
import { LeadCaptureCTA } from '../components/LeadCapture';
import { LeadInterestPopup } from '../components/LeadPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Utility function to calculate SHA-256 hash of a file
async function calculateFileHash(file) {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function AnalyzeQuote() {
  const { user, login, isPremium, getAuthHeaders } = useAuth();
  const { startLoading, stopLoading } = useLeonLoading();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Ad Gate - vidéo pub avant analyse (users gratuits)
  const { requireAd, AdGateModal, isAdShowing } = useAdGate('analysis');
  
  // DEBUG: Log auth status on mount
  useEffect(() => {
    const token = localStorage.getItem('abl_session_token');
    console.log(`[AnalyzeQuote] Component mounted. User: ${user?.email || 'null'}`);
    console.log(`[AnalyzeQuote] Token in localStorage: ${token ? token.substring(0, 25) + '...' : 'NULL'}`);
  }, [user]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(''); // Pour afficher la progression
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [showValidatedAdmin, setShowValidatedAdmin] = useState(false);
  const [emailGenerated, setEmailGenerated] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [pdfSuccess, setPdfSuccess] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [loadingFromLeon, setLoadingFromLeon] = useState(false);
  const [leonRequest, setLeonRequest] = useState(null);
  
  // États pour la détection de doublons
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  
  // État pour la popup de lead
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [duplicateFound, setDuplicateFound] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Tutoriel Léon
  const {
    config: tutorialConfig,
    showWelcome,
    showTutorial,
    startTutorial,
    dismissWelcome,
    completeTutorial,
    closeTutorial,
    restartTutorial,
    disableAllTutorials
  } = useTutorial('analyze');

  // Check if tutorial was requested via URL
  useEffect(() => {
    if (location.state?.startTutorial) {
      restartTutorial();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Check for request_id from Léon Hub (continuity flow)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestId = params.get('request_id');
    if (requestId) {
      loadLeonRequest(requestId);
      // Clean URL without reloading
      window.history.replaceState({}, document.title, '/analyze');
    }
  }, [location.search]);

  // Load request from Léon Hub with attached file
  const loadLeonRequest = async (requestId) => {
    setLoadingFromLeon(true);
    setAnalysisStep('Récupération de votre demande...');
    
    try {
      const response = await fetch(`${API_URL}/api/leon/requests/${requestId}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        console.error('Failed to fetch request:', response.status);
        throw new Error('Demande introuvable');
      }
      
      const requestData = await response.json();
      console.log('[LEON] Request loaded:', requestData.request_id, 'files:', requestData.attached_files?.length);
      setLeonRequest(requestData);
      
      // Check if there are attached files
      if (requestData.attached_files && requestData.attached_files.length > 0) {
        const fileData = requestData.attached_files[0];
        
        // Convert base64 data URI to File object
        if (typeof fileData === 'string' && fileData.startsWith('data:')) {
          setAnalysisStep('Chargement de votre devis...');
          
          // Extract mime type and base64 data
          const matches = fileData.match(/^data:([^;]+);base64,(.+)$/);
          if (matches) {
            const mimeType = matches[1];
            const base64Data = matches[2];
            
            try {
              // Convert base64 to blob
              const byteCharacters = atob(base64Data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], { type: mimeType });
              
              // Create File object with a meaningful name
              const fileName = requestData.prompt?.toLowerCase().includes('devis') 
                ? 'devis.pdf' 
                : `document_${requestId.slice(-6)}.pdf`;
              const fileObj = new File([blob], fileName, { type: mimeType });
              
              console.log('[LEON] File created:', fileObj.name, 'size:', fileObj.size);
              setFile(fileObj);
              setLoadingFromLeon(false);
              
              // Set preview for images
              if (mimeType.startsWith('image/')) {
                setPreview(fileData);
              }
              
              // Auto-start analysis after a short delay
              setAnalysisStep('Léon analyse votre devis...');
              handleAnalyzeWithFile(fileObj, requestData.prompt);
              return; // Exit here - analysis is running
            } catch (decodeErr) {
              console.error('[LEON] Failed to decode file:', decodeErr);
              setError('Le fichier n\'a pas pu être décodé');
            }
          } else {
            console.error('[LEON] Invalid data URI format');
            setError('Format de fichier invalide');
          }
        } else {
          console.error('[LEON] File data is not a data URI');
          setError('Format de fichier non supporté');
        }
      } else {
        setError('Aucun fichier trouvé dans la demande');
      }
    } catch (err) {
      console.error('Error loading Léon request:', err);
      setError('Impossible de charger la demande depuis Léon');
    }
    setLoadingFromLeon(false);
  };

  // Analyze with a specific file (used for Léon continuity)
  const handleAnalyzeWithFile = async (fileToAnalyze, userPrompt = null) => {
    if (!fileToAnalyze) return;
    
    setAnalyzing(true);
    setError(null);
    startLoading('analyse_devis');
    
    const formData = new FormData();
    formData.append('file', fileToAnalyze);
    if (userPrompt) {
      formData.append('user_prompt', userPrompt);
    }
    
    const timestamp = Date.now();
    console.log(`[ANALYZE ${timestamp}] Starting analysis for file:`, fileToAnalyze.name, 'Size:', fileToAnalyze.size);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`[ANALYZE ${timestamp}] TIMEOUT after 180s - aborting request`);
      controller.abort();
    }, 180000);
    
    try {
      setAnalysisStep('Upload du fichier...');
      console.log(`[ANALYZE ${timestamp}] Step: Upload du fichier`);
      
      // Get auth headers for token-based authentication
      const authHeaders = getAuthHeaders();
      // Remove Content-Type as it will be set automatically for FormData
      delete authHeaders['Content-Type'];
      
      console.log(`[ANALYZE ${timestamp}] Calling ${API_URL}/api/quotes/analyze with Authorization header`);
      
      const response = await fetch(`${API_URL}/api/quotes/analyze`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: authHeaders,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log(`[ANALYZE ${timestamp}] Response received: status=${response.status}`);
      
      if (response.status === 403) {
        const data = await response.json();
        console.log(`[ANALYZE ${timestamp}] 403 Forbidden:`, data.detail);
        setError(data.detail);
        return;
      }
      
      if (response.status === 401) {
        console.log(`[ANALYZE ${timestamp}] 401 Unauthorized - session may have expired`);
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.log(`[ANALYZE ${timestamp}] Error response:`, errorData);
        throw new Error(errorData.detail || 'Erreur lors de l\'analyse');
      }
      
      setAnalysisStep('Traitement de la réponse...');
      console.log(`[ANALYZE ${timestamp}] Step: Parsing JSON response`);
      
      let data;
      try {
        const responseText = await response.text();
        console.log(`[ANALYZE ${timestamp}] Response text length: ${responseText.length}`);
        data = JSON.parse(responseText);
        console.log(`[ANALYZE ${timestamp}] Parsed successfully. Keys:`, Object.keys(data).join(', '));
      } catch (parseError) {
        console.error(`[ANALYZE ${timestamp}] JSON parse error:`, parseError);
        throw new Error('Erreur lors du traitement de la réponse du serveur');
      }
      
      console.log(`[ANALYZE ${timestamp}] Analysis complete! Score: ${data.global_score}, ID: ${data.analysis_id}`);
      console.log(`[ANALYZE ${timestamp}] Setting analysis state...`);
      setAnalysis(data);
      console.log(`[ANALYZE ${timestamp}] Analysis state SET - UI should update now`);
      
      // Show lead popup after analysis (only for free users, and only if not already submitted)
      if (!isPremium && !leadSubmitted) {
        setTimeout(() => {
          setShowLeadPopup(true);
        }, 2000); // Delay to let user see the results first
      }
      
      // Update Léon request status if we came from there
      if (leonRequest?.request_id) {
        try {
          const updateHeaders = getAuthHeaders();
          await fetch(`${API_URL}/api/leon/requests/${leonRequest.request_id}/complete`, {
            method: 'PATCH',
            headers: updateHeaders,
            credentials: 'include',
            body: JSON.stringify({ 
              result_id: data.analysis_id,
              status: 'completed'
            })
          });
        } catch (updateErr) {
          console.warn('Could not update Léon request status:', updateErr);
        }
      }
      
    } catch (err) {
      clearTimeout(timeoutId);
      console.log(`[ANALYZE ${timestamp}] CATCH - Error:`, err.name, err.message);
      
      if (err.name === 'AbortError') {
        console.log(`[ANALYZE ${timestamp}] Request was aborted (timeout or manual cancel)`);
        setError('L\'analyse a pris trop de temps (> 3 minutes). Le fichier est peut-être trop volumineux. Veuillez réessayer avec un document plus léger.');
      } else if (err.message?.includes('network') || err.message?.includes('fetch')) {
        console.log(`[ANALYZE ${timestamp}] Network error`);
        setError('Erreur de connexion au serveur. Vérifiez votre connexion internet et réessayez.');
      } else {
        console.error('[ANALYZE] Error:', err);
        // Provide helpful error message when coming from Léon
        if (leonRequest) {
          setError(`Nous n'avons pas réussi à analyser ce devis automatiquement. Vous pouvez :\n• Réessayer l'analyse (parfois un simple réessai suffit)\n• Envoyer une capture d'écran du devis\n• Envoyer les pages une par une si le document est long`);
        } else {
          setError(err.message || 'Erreur lors de l\'analyse');
        }
      }
    } finally {
      stopLoading();
      setAnalyzing(false);
      setAnalysisStep('');
      console.log(`[ANALYZE ${timestamp}] Finally block - cleaning up`);
    }
  };

  // Load existing analysis if ID is provided
  useEffect(() => {
    if (id) {
      loadExistingAnalysis(id);
    }
  }, [id]);

  const loadExistingAnalysis = async (analysisId) => {
    setLoadingExisting(true);
    console.log(`[ANALYZE] Loading existing analysis: ${analysisId}`);
    try {
      const response = await fetch(`${API_URL}/api/quotes/analyses/${analysisId}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        setError('Analyse introuvable');
      }
    } catch (err) {
      setError('Erreur lors du chargement de l\'analyse');
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFile = (selectedFile) => {
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Format non supporté. Veuillez utiliser PDF, JPEG, PNG ou WEBP.');
      return;
    }
    
    setFile(selectedFile);
    setError(null);
    setAnalysis(null);
    
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  // Fonction pour vérifier si un fichier est un doublon
  const checkForDuplicate = async (fileToCheck) => {
    try {
      setCheckingDuplicate(true);
      
      // Calculer le hash du fichier
      const fileHash = await calculateFileHash(fileToCheck);
      
      const formData = new FormData();
      formData.append('file_hash', fileHash);
      formData.append('file_size', fileToCheck.size.toString());
      formData.append('filename', fileToCheck.name);
      
      // Get auth headers for token-based authentication
      const authHeaders = getAuthHeaders();
      delete authHeaders['Content-Type']; // Let browser set for FormData
      
      // DEBUG: Log token status
      const tokenFromStorage = localStorage.getItem('abl_session_token');
      console.log(`[DUPLICATE] Token from localStorage: ${tokenFromStorage ? tokenFromStorage.substring(0, 25) + '...' : 'NULL'}`);
      console.log(`[DUPLICATE] Headers being sent:`, JSON.stringify(authHeaders));
      
      const response = await fetch(`${API_URL}/api/quotes/check-duplicate`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      return { is_duplicate: false };
    } catch (err) {
      console.error('[DUPLICATE] Check failed:', err);
      return { is_duplicate: false };
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // Fonction pour analyser le fichier (après vérification doublon ou si l'utilisateur veut analyser quand même)
  const performAnalysis = async (fileToAnalyze) => {
    setAnalyzing(true);
    setError(null);
    setShowDuplicateModal(false);
    startLoading('analyse_devis');
    
    const formData = new FormData();
    formData.append('file', fileToAnalyze);
    
    const timestamp = Date.now();
    console.log(`[ANALYZE ${timestamp}] performAnalysis - Starting for:`, fileToAnalyze.name, 'Size:', fileToAnalyze.size);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`[ANALYZE ${timestamp}] TIMEOUT after 180s`);
      controller.abort();
    }, 180000);
    
    try {
      // Get auth headers for token-based authentication
      const authHeaders = getAuthHeaders();
      delete authHeaders['Content-Type']; // Let browser set for FormData
      
      console.log(`[ANALYZE ${timestamp}] Headers being sent:`, JSON.stringify(authHeaders));
      console.log(`[ANALYZE ${timestamp}] Calling ${API_URL}/api/quotes/analyze`);
      
      const response = await fetch(`${API_URL}/api/quotes/analyze`, {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders,
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log(`[ANALYZE ${timestamp}] Response status:`, response.status);
      
      if (response.status === 401) {
        console.log(`[ANALYZE ${timestamp}] 401 - Session expired`);
        setError('Votre session a expiré. Veuillez vous reconnecter.');
        return;
      }
      
      if (response.status === 403) {
        const data = await response.json();
        setError(data.detail || 'Limite d\'analyses atteinte.');
        return;
      }
      
      if (response.status === 400) {
        const data = await response.json();
        setError(data.detail || 'Le fichier n\'a pas pu être traité.');
        return;
      }
      
      if (response.status === 429) {
        setError('Trop de demandes en cours. Veuillez patienter quelques secondes et réessayer.');
        return;
      }
      
      if (response.status === 504) {
        setError('L\'analyse a pris trop de temps. Essayez avec une image plus légère ou une capture d\'écran.');
        return;
      }
      
      if (response.status === 500) {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Erreur temporaire du moteur d\'analyse. Réessayez ou utilisez une capture d\'écran du devis.');
        return;
      }
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const errorMsg = data.detail || 'Erreur lors de l\'analyse';
        console.error('[ANALYZE] Error:', errorMsg);
        setError(errorMsg);
        return;
      }
      
      const data = await response.json();
      console.log(`[ANALYZE ${timestamp}] Success:`, data.analysis_id);
      setAnalysis(data);
      navigate(`/analyze/${data.analysis_id}`, { replace: true });
    } catch (err) {
      console.log(`[ANALYZE ${timestamp}] CATCH:`, err.name, err.message);
      if (err.name === 'AbortError') {
        setError('L\'analyse a pris trop de temps (> 3 minutes). Le fichier est peut-être trop volumineux. Réessayez avec un document plus léger.');
      } else {
        console.error(`[ANALYZE ${timestamp}] Fetch error:`, err);
        setError('Erreur de connexion. Vérifiez votre connexion internet.');
      }
    } finally {
      setAnalyzing(false);
      stopLoading();
      console.log(`[ANALYZE ${timestamp}] Finally - cleanup done`);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    
    // Vérifier d'abord si c'est un doublon
    setError(null);
    const duplicateResult = await checkForDuplicate(file);
    
    if (duplicateResult.is_duplicate) {
      // Doublon détecté - afficher la modale
      setDuplicateFound(duplicateResult);
      setShowDuplicateModal(true);
      return;
    }
    
    // Pas de doublon - passer par la pub (si user gratuit)
    requireAd(() => performAnalysis(file));
  };

  // Fonction pour aller voir l'analyse existante
  const handleViewExistingAnalysis = () => {
    if (duplicateFound?.existing_analysis?.analysis_id) {
      navigate(`/analyze/${duplicateFound.existing_analysis.analysis_id}`);
      setShowDuplicateModal(false);
    }
  };

  // Fonction pour analyser quand même malgré le doublon
  const handleAnalyzeAnyway = async () => {
    requireAd(() => performAnalysis(file));
    setShowDuplicateModal(false);
  };

  // (Code nettoyé - ancienne fonction handleAnalyzeOld supprimée)

  const getScoreColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-500', text: 'text-green-500', label: 'Devis fiable' };
    if (score >= 60) return { bg: 'bg-orange-500', text: 'text-orange-500', label: 'Devis à vérifier' };
    return { bg: 'bg-red-500', text: 'text-red-500', label: 'Devis risqué' };
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'conforme':
      case 'présent':
      case 'present':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'attention':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'manquant':
      case 'absent':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-zinc-500" />;
    }
  };

  const administrativeItems = [
    { key: 'company_name', label: 'Nom de l\'entreprise', icon: Building2 },
    { key: 'address', label: 'Adresse', icon: MapPin },
    { key: 'siret', label: 'Numéro SIRET', icon: FileCheck },
    { key: 'decennial_insurance', label: 'Mention assurance décennale', icon: Shield },
    { key: 'payment_conditions', label: 'Conditions de paiement', icon: CreditCard },
    { key: 'tva', label: 'TVA', icon: CircleDollarSign },
    { key: 'quote_date', label: 'Date du devis', icon: FileCheck },
    { key: 'validity_duration', label: 'Durée de validité', icon: FileCheck },
  ];

  const technicalItems = [
    { key: 'work_coherence', label: 'Cohérence des postes travaux', icon: Wrench },
    { key: 'missing_items', label: 'Détection des postes manquants', icon: ListChecks },
    { key: 'services_check', label: 'Vérification des prestations', icon: ClipboardCheck },
  ];

  // Extract anomalies from analysis
  const getAnomalies = () => {
    const anomalies = [];
    
    if (analysis?.administrative_analysis) {
      Object.entries(analysis.administrative_analysis).forEach(([key, value]) => {
        if (value === 'manquant' || value === 'absent' || value === 'attention') {
          const item = administrativeItems.find(i => i.key === key);
          anomalies.push({
            type: value === 'attention' ? 'warning' : 'error',
            message: item ? `${item.label} ${value === 'attention' ? 'à vérifier' : 'manquant(e)'}` : `${key} ${value}`
          });
        }
      });
    }
    
    if (analysis?.technical_analysis?.alerts) {
      analysis.technical_analysis.alerts.forEach(alert => {
        anomalies.push({ type: 'warning', message: alert });
      });
    }
    
    if (analysis?.technical_analysis?.missing_elements) {
      analysis.technical_analysis.missing_elements.forEach(item => {
        anomalies.push({ type: 'error', message: item });
      });
    }
    
    if (analysis?.price_analysis?.items) {
      analysis.price_analysis.items.forEach(item => {
        if (item.assessment === 'élevé' || item.evaluation === 'élevé') {
          anomalies.push({ 
            type: 'warning', 
            message: `Prix ${item.name || item.item} supérieur au marché` 
          });
        }
      });
    }
    
    return anomalies;
  };

  // Get recommendations
  const getRecommendations = () => {
    const recommendations = [];
    
    if (analysis?.report?.questions_for_artisan) {
      recommendations.push(...analysis.report.questions_for_artisan);
    }
    
    // Add default recommendations based on analysis
    const anomalies = getAnomalies();
    if (anomalies.some(a => a.message.includes('assurance décennale'))) {
      if (!recommendations.includes('Demander le numéro d\'assurance décennale')) {
        recommendations.push('Demander le numéro d\'assurance décennale');
      }
    }
    
    if (anomalies.length > 0 && !recommendations.includes('Comparer avec un autre devis')) {
      recommendations.push('Comparer avec un autre devis');
    }
    
    return recommendations.slice(0, 5);
  };

  // Generate AI summary based on analysis
  const getAISummary = () => {
    if (!analysis) return '';
    
    const score = analysis.global_score;
    const anomalies = getAnomalies();
    const missingCount = anomalies.filter(a => a.type === 'error').length;
    const warningCount = anomalies.filter(a => a.type === 'warning').length;
    
    if (score >= 80) {
      return "Ce devis est globalement bien structuré. Les mentions obligatoires sont présentes et les prix correspondent aux moyennes du marché. Vous pouvez envisager de signer après avoir vérifié les derniers détails avec l'artisan.";
    } else if (score >= 60) {
      return `Ce devis présente ${missingCount > 0 ? 'des éléments manquants' : 'quelques points d\'incertitude'}${warningCount > 0 ? ' et certaines prestations ne sont pas suffisamment détaillées' : ''}. Une clarification auprès de l'artisan est recommandée avant signature.`;
    } else {
      return `Ce devis présente des risques significatifs avec ${missingCount} élément(s) manquant(s) et ${warningCount} point(s) d'attention. Il est fortement recommandé de demander des précisions ou de comparer avec d'autres devis avant de vous engager.`;
    }
  };

  // Calculate estimated overrun risk
  const getOverrunRisk = () => {
    if (!analysis) return null;
    
    const missingElements = analysis.technical_analysis?.missing_elements || [];
    const risks = analysis.technical_analysis?.risks || [];
    const priceItems = analysis.price_analysis?.items || [];
    
    // Base calculation on issues found
    let minRisk = 0;
    let maxRisk = 0;
    
    // Each missing element adds potential risk
    minRisk += missingElements.length * 150;
    maxRisk += missingElements.length * 500;
    
    // Each risk factor adds more
    minRisk += risks.length * 200;
    maxRisk += risks.length * 600;
    
    // Price anomalies
    priceItems.forEach(item => {
      if (item.evaluation === 'bas' || item.assessment === 'bas') {
        minRisk += 100;
        maxRisk += 400;
      }
    });
    
    // If no issues, return null
    if (minRisk === 0) return null;
    
    // Cap the values reasonably
    minRisk = Math.min(minRisk, 2000);
    maxRisk = Math.min(maxRisk, 5000);
    
    return { min: minRisk, max: maxRisk };
  };

  // Get admin items split by status
  const getAdminItemsByStatus = () => {
    const validated = [];
    const issues = [];
    
    administrativeItems.forEach(item => {
      const status = analysis?.administrative_analysis?.[item.key];
      if (status === 'conforme' || status === 'présent' || status === 'present') {
        validated.push({ ...item, status });
      } else {
        issues.push({ ...item, status });
      }
    });
    
    return { validated, issues };
  };

  // Generate email content - structured and professional
  const generateArtisanEmail = () => {
    const anomaliesWithSeverity = getAnomaliesWithSeverity();
    const criticalPoints = anomaliesWithSeverity.filter(a => a.severity === 'critical');
    const importantPoints = anomaliesWithSeverity.filter(a => a.severity === 'important');
    const clarifyPoints = anomaliesWithSeverity.filter(a => a.severity === 'clarify');
    const questions = analysis?.report?.questions_for_artisan || [];
    
    const hasAnomalies = anomaliesWithSeverity.length > 0 || questions.length > 0;
    
    let emailBody = `Bonjour,\n\nMerci pour votre devis.\n\n`;
    
    if (hasAnomalies) {
      emailBody += `Avant de pouvoir le valider, j'aurais besoin de quelques précisions sur certains points relevés :\n\n`;
      
      if (criticalPoints.length > 0) {
        criticalPoints.forEach((p) => {
          emailBody += `• ${p.message}\n`;
        });
      }
      
      if (importantPoints.length > 0) {
        importantPoints.forEach((p) => {
          emailBody += `• ${p.message}\n`;
        });
      }
      
      if (clarifyPoints.length > 0) {
        clarifyPoints.forEach((p) => {
          emailBody += `• ${p.message}\n`;
        });
      }
      
      if (questions.length > 0) {
        questions.slice(0, 5).forEach((q) => {
          emailBody += `• ${q}\n`;
        });
      }
      
      emailBody += `\nPouvez-vous me confirmer ces éléments et, si nécessaire, me transmettre une version mise à jour du devis ?\n\n`;
    } else {
      emailBody += `J'ai bien reçu votre devis et il me semble complet. Avant de le valider définitivement, pourriez-vous simplement me confirmer :\n\n`;
      emailBody += `• La disponibilité pour démarrer les travaux\n`;
      emailBody += `• La durée estimée du chantier\n`;
      emailBody += `• Les modalités de paiement\n\n`;
    }
    
    emailBody += `Merci par avance.\n\nCordialement`;
    
    return {
      subject: 'Demande de précisions sur le devis travaux',
      body: emailBody
    };
  };

  // Get the full email text with subject
  const getFullEmailText = () => {
    const email = generateArtisanEmail();
    return `Objet : ${email.subject}\n\n${email.body}`;
  };

  // Copy email to clipboard with fallback
  const handleCopyEmail = async () => {
    const emailText = getFullEmailText();
    
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(emailText);
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 3000);
        return;
      } catch (err) {
        console.error('Clipboard API failed:', err);
      }
    }
    
    // Fallback: use textarea + execCommand
    try {
      const textArea = document.createElement('textarea');
      textArea.value = emailText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        setEmailCopied(true);
        setTimeout(() => setEmailCopied(false), 3000);
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
  };

  // Open email in mail client
  const handleOpenMailClient = () => {
    const email = generateArtisanEmail();
    const mailtoLink = `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
    window.open(mailtoLink, '_blank');
  };

  // Open email modal
  const handleShowEmailModal = () => {
    setShowEmailModal(true);
    setEmailCopied(false);
  };

  // PDF download states
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);
  
  // Prepare PDF for download - gets a direct download URL from backend
  const handlePreparePDF = async () => {
    if (!disclaimerAccepted || !analysis) {
      return;
    }
    
    setDownloadingPDF(true);
    setPdfSuccess(false);
    setPdfError(false);
    setPdfDownloadUrl(null);
    
    try {
      // Request a temporary download token from backend
      const tokenResponse = await fetch(
        `${API_URL}/api/quotes/analyses/${analysis.analysis_id}/pdf-token`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!tokenResponse.ok) {
        throw new Error('Erreur lors de la préparation du PDF');
      }
      
      const { token } = await tokenResponse.json();
      
      // Create direct download URL
      const downloadUrl = `${API_URL}/api/quotes/download/${token}`;
      setPdfDownloadUrl(downloadUrl);
      setPdfSuccess(true);
      
    } catch (error) {
      console.error('PDF preparation error:', error);
      setPdfError(true);
      setTimeout(() => setPdfError(false), 5000);
    } finally {
      setDownloadingPDF(false);
    }
  };

  // Get positive points of the quote
  const getPositivePoints = () => {
    const positives = [];
    
    // Check administrative compliance
    const adminValidated = getAdminItemsByStatus().validated;
    if (adminValidated.length >= 5) {
      positives.push('Structure administrative conforme');
    }
    
    // Check if company info is present
    if (analysis?.administrative_analysis?.company_name === 'conforme') {
      positives.push('Entreprise clairement identifiée');
    }
    
    // Check technical coherence
    if (analysis?.technical_analysis?.coherence === 'conforme' || 
        analysis?.technical_analysis?.verifications?.includes('cohérence')) {
      positives.push('Prestations techniques cohérentes');
    }
    
    // Check prices
    const priceItems = analysis?.price_analysis?.items || [];
    const correctPrices = priceItems.filter(p => p.assessment === 'correct' || p.evaluation === 'correct');
    if (correctPrices.length > 0) {
      positives.push('Prix conformes au marché pour plusieurs postes');
    }
    
    // Check if total is reasonable
    if (analysis?.price_analysis?.global_assessment === 'correct' || 
        analysis?.price_analysis?.market_comparison?.includes('correct')) {
      positives.push('Montant global dans la moyenne du marché');
    }
    
    // Default positives based on score
    if (analysis?.global_score >= 50) {
      if (!positives.some(p => p.includes('Prestations'))) {
        positives.push('Prestations principales identifiées');
      }
    }
    
    if (positives.length === 0) {
      positives.push('Devis reçu pour analyse');
    }
    
    return positives.slice(0, 4);
  };

  // Get anomalies with severity classification
  const getAnomaliesWithSeverity = () => {
    const anomalies = [];
    
    // Administrative issues - Critical
    if (analysis?.administrative_analysis) {
      const criticalKeys = ['insurance_decennale', 'siret', 'company_name'];
      const importantKeys = ['payment_conditions', 'validity_date', 'quote_date'];
      
      Object.entries(analysis.administrative_analysis).forEach(([key, value]) => {
        if (value === 'manquant' || value === 'absent' || value === 'attention') {
          const item = administrativeItems.find(i => i.key === key);
          let severity = 'clarify';
          if (criticalKeys.includes(key)) severity = 'critical';
          else if (importantKeys.includes(key)) severity = 'important';
          
          anomalies.push({
            severity,
            type: value === 'attention' ? 'warning' : 'error',
            message: item ? `${item.label} ${value === 'attention' ? 'à vérifier' : 'manquant(e)'}` : `${key} ${value}`
          });
        }
      });
    }
    
    // Technical alerts - Important
    if (analysis?.technical_analysis?.alerts) {
      analysis.technical_analysis.alerts.forEach(alert => {
        anomalies.push({ severity: 'important', type: 'warning', message: alert });
      });
    }
    
    // Missing elements - Critical
    if (analysis?.technical_analysis?.missing_elements) {
      analysis.technical_analysis.missing_elements.forEach(item => {
        anomalies.push({ severity: 'critical', type: 'error', message: item });
      });
    }
    
    // Price anomalies - Clarify
    if (analysis?.price_analysis?.items) {
      analysis.price_analysis.items.forEach(item => {
        if (item.assessment === 'élevé' || item.evaluation === 'élevé') {
          anomalies.push({ 
            severity: 'clarify',
            type: 'warning', 
            message: `Prix ${item.name || item.item} supérieur au marché` 
          });
        }
      });
    }
    
    // Sort by severity
    const severityOrder = { critical: 0, important: 1, clarify: 2 };
    return anomalies.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  };

  // Get detail level of the quote
  const getDetailLevel = () => {
    let score = 0;
    let maxScore = 3;
    
    // Check for measurements (métrés)
    const hasMetres = analysis?.technical_analysis?.verifications?.some(v => 
      v.toLowerCase().includes('métré') || v.toLowerCase().includes('surface')
    );
    if (hasMetres) score++;
    
    // Check for technical details
    const hasDetails = analysis?.price_analysis?.items?.some(item => 
      item.description && item.description.length > 20
    );
    if (hasDetails) score++;
    
    // Check for supply vs labor distinction
    const hasDistinction = analysis?.price_analysis?.items?.some(item =>
      item.supply_included !== undefined || 
      item.description?.toLowerCase().includes('fourniture') ||
      item.description?.toLowerCase().includes('main')
    );
    if (hasDistinction) score++;
    
    // Also check based on global score
    if (analysis?.global_score >= 70) score = Math.max(score, 2);
    else if (analysis?.global_score >= 50) score = Math.max(score, 1);
    
    if (score >= 3) return { level: 'Élevé', color: 'text-green-500', bg: 'bg-green-500/10' };
    if (score >= 2) return { level: 'Moyen', color: 'text-orange-500', bg: 'bg-orange-500/10' };
    return { level: 'Faible', color: 'text-red-500', bg: 'bg-red-500/10' };
  };

  // Get Phenix advice
  const getPhenixAdvice = () => {
    const score = analysis?.global_score || 0;
    const anomaliesCount = getAnomaliesWithSeverity().length;
    const criticalCount = getAnomaliesWithSeverity().filter(a => a.severity === 'critical').length;
    
    if (score >= 80) {
      return "Ce devis est prêt pour signature. Les éléments essentiels sont présents et les prix sont conformes au marché.";
    } else if (score >= 60 && criticalCount === 0) {
      return "Si l'artisan répond favorablement aux questions soulevées, ce devis pourra être considéré comme fiable pour signature.";
    } else if (score >= 60) {
      return `Si les ${criticalCount} élément(s) critique(s) sont corrigés et que l'artisan répond aux questions, ce devis pourra être validé.`;
    } else {
      return "Ce devis nécessite des modifications substantielles. Nous vous recommandons de demander une version révisée ou de consulter un autre artisan.";
    }
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20 sm:pt-24 pb-8 sm:pb-12 px-4 sm:px-6">
      {/* Le système de chargement Léon est maintenant géré globalement via LeonLoadingProvider */}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            Audit de devis travaux
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            {analysis ? `Résultats de l'audit : ${analysis.filename || 'Devis'}` : 'Téléchargez votre devis et obtenez un audit détaillé pour identifier incohérences, oublis et écarts de prix.'}
          </p>
        </motion.div>

        {/* Loading state for existing analysis */}
        {loadingExisting && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin mb-4" />
            <p className="text-zinc-400">Chargement de l'analyse...</p>
          </div>
        )}

        {/* Loading state from Léon Hub - Special immersive UX */}
        {(loadingFromLeon || (analyzing && leonRequest)) && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="relative mb-8">
              {/* Glowing ring around Leon */}
              <div className="absolute inset-0 w-32 h-32 -m-4 rounded-full bg-gold-500/20 blur-xl animate-pulse" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-gold-500/20 to-amber-500/10 border-2 border-gold-500/40 flex items-center justify-center">
                <LeonAvatar className="w-16 h-16" />
              </div>
            </div>
            
            <motion.h2 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-xl font-heading font-bold text-white mb-2"
            >
              Léon analyse votre devis...
            </motion.h2>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-zinc-400 mb-6"
            >
              {analysisStep || 'Préparation de l\'analyse...'}
            </motion.p>
            
            {/* Show file name if available */}
            {file && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex items-center gap-3 px-4 py-3 bg-zinc-900/80 border border-zinc-700 rounded-xl"
              >
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-white font-medium">{file.name}</p>
                  <p className="text-xs text-zinc-500">
                    {(file.size / 1024).toFixed(0)} Ko • Analyse en cours
                  </p>
                </div>
                <Loader2 className="w-5 h-5 text-gold-500 animate-spin ml-2" />
              </motion.div>
            )}
            
            {/* Original user message */}
            {leonRequest?.prompt && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="mt-6 text-sm text-zinc-500 italic max-w-md text-center"
              >
                "{leonRequest.prompt}"
              </motion.p>
            )}
          </motion.div>
        )}

        <AnimatePresence mode="wait">
          {!analysis && !loadingExisting && !loadingFromLeon && !(analyzing && leonRequest) ? (
            <motion.div
              key="upload"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Upload Zone */}
              <div className="glass-card rounded-3xl p-6 sm:p-8 md:p-12 text-center transition-all">
                {file ? (
                  <div className="space-y-6">
                    {preview ? (
                      <img 
                        src={preview} 
                        alt="Aperçu" 
                        className="max-h-64 mx-auto rounded-xl shadow-2xl border border-white/10"
                      />
                    ) : (
                      <div className="w-24 h-24 mx-auto bg-gold-500/20 rounded-2xl flex items-center justify-center">
                        <FileIcon className="w-12 h-12 text-gold-500" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-heading font-bold text-xl">{file.name}</p>
                      <p className="text-zinc-500 text-sm mt-1">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button 
                      variant="outline"
                      className="border-zinc-700 text-zinc-400 hover:text-white"
                      onClick={() => { setFile(null); setPreview(null); }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Changer de fichier
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Mobile: MobilePhotoUpload avec caméra native */}
                    <div className="sm:hidden mb-6">
                      <MobilePhotoUpload
                        onImageSelect={(selectedFile, previewUrl) => {
                          handleFile(selectedFile);
                        }}
                        onImageRemove={() => { setFile(null); setPreview(null); }}
                        currentImage={null}
                        label="Prenez une photo ou sélectionnez votre devis"
                        variant="full"
                        cameraFirst={true}
                        accept="image/*,application/pdf"
                        maxSize={10}
                        disabled={analyzing}
                      />
                      <p className="text-zinc-500 text-xs mt-3">
                        Ou glissez un PDF depuis votre gestionnaire de fichiers
                      </p>
                    </div>

                    {/* Desktop: Zone drag & drop classique */}
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      data-testid="upload-zone"
                      className={`hidden sm:block relative border-2 border-dashed rounded-2xl p-8 transition-all ${
                        dragActive 
                          ? 'border-gold-500 bg-gold-500/10' 
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        data-testid="analyze-file-input"
                      />
                      
                      <div className="w-20 h-20 mx-auto bg-gold-500/10 rounded-2xl flex items-center justify-center mb-6">
                        <Upload className="w-10 h-10 text-gold-500" />
                      </div>
                      
                      <h3 className="text-xl font-heading font-bold text-white mb-3">
                        Importer votre devis
                      </h3>
                      
                      <p className="text-zinc-400 mb-6">
                        Glissez votre fichier ici ou cliquez pour parcourir
                      </p>
                      
                      {/* Formats acceptés */}
                      <div className="flex items-center justify-center gap-6 mb-6">
                        <div className="flex items-center gap-2 text-zinc-500">
                          <FileIcon className="w-5 h-5" />
                          <span className="text-sm">PDF</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-500">
                          <Image className="w-5 h-5" />
                          <span className="text-sm">Image</span>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-500">
                          <Camera className="w-5 h-5" />
                          <span className="text-sm">Photo</span>
                        </div>
                      </div>
                      
                      <Button 
                        className="bg-gold-500 hover:bg-gold-400 text-black font-bold px-8 py-5 text-lg"
                        onClick={(e) => {
                          e.stopPropagation();
                          document.querySelector('[data-testid="analyze-file-input"]').click();
                        }}
                      >
                        <Upload className="w-5 h-5 mr-2" />
                        Télécharger mon devis
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {/* Error avec options de reprise */}
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 p-5 bg-red-500/10 border border-red-500/30 rounded-xl"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-red-400 font-semibold mb-3">{error}</p>
                      
                      {/* Message de secours */}
                      <div className="p-4 bg-zinc-800/50 rounded-lg mb-4">
                        <p className="text-zinc-300 text-sm mb-2">
                          Nous n'avons pas réussi à analyser ce devis automatiquement. Vous pouvez :
                        </p>
                        <ul className="text-zinc-400 text-sm list-disc list-inside space-y-1">
                          <li>Réessayer l'analyse (parfois un simple réessai suffit)</li>
                          <li>Envoyer une capture d'écran du devis</li>
                          <li>Envoyer les pages une par une si le document est long</li>
                        </ul>
                      </div>
                      
                      {/* Boutons de reprise */}
                      <div className="flex flex-wrap gap-3">
                        <Button
                          onClick={handleAnalyze}
                          disabled={analyzing || !file}
                          className="bg-gold-500 hover:bg-gold-400 text-black font-bold"
                          data-testid="retry-analysis-btn"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Réessayer l'analyse
                        </Button>
                        
                        <Button
                          onClick={() => {
                            setFile(null);
                            setPreview(null);
                            setError(null);
                          }}
                          variant="outline"
                          className="border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                          data-testid="load-another-file-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Charger un autre fichier
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Analyze Button */}
              {file && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-8"
                >
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing || checkingDuplicate}
                    className="w-full py-8 bg-gold-500 hover:bg-gold-400 text-black font-bold text-xl rounded-xl disabled:opacity-50"
                    data-testid="analyze-submit-btn"
                  >
                    {checkingDuplicate ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span>Vérification en cours...</span>
                        </div>
                        <span className="text-sm font-normal opacity-80">Recherche d'une analyse existante</span>
                      </div>
                    ) : analyzing ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span>{analysisStep || 'Analyse en cours...'}</span>
                        </div>
                        <span className="text-sm font-normal opacity-80">Veuillez patienter quelques instants</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <FileSearch className="w-6 h-6" />
                        <span>Lancer l'audit du devis</span>
                      </div>
                    )}
                  </Button>
                </motion.div>
              )}

              {/* Léon Tips */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-12 glass-card p-6 flex items-start gap-4"
              >
                <LeonAvatar className="w-16 h-16 flex-shrink-0" />
                <div>
                  <p className="text-gold-500 font-heading font-bold mb-2">
                    Conseil de Léon
                  </p>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    Pour une meilleure analyse, assure-toi que ton devis est lisible et complet. 
                    Je vais vérifier les mentions légales, les prix et la cohérence technique pour toi !
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ) : analysis ? (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6 md:space-y-8"
            >
              {/* Header avec nom du fichier */}
              <div className="glass-card p-4 md:p-6 border-gold-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gold-500/10 flex items-center justify-center">
                      <FileText className="w-5 h-5 md:w-6 md:h-6 text-gold-500" />
                    </div>
                    <div>
                      <h2 className="text-lg md:text-xl font-heading font-bold text-white">
                        Rapport d'analyse expert
                      </h2>
                      <p className="text-sm text-zinc-400">{analysis.filename || 'Devis analysé'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Nouveau Rapport Enrichi avec Accordéons */}
              {analysis.executive_summary || analysis.detected_risks || analysis.questions_for_artisan ? (
                <EnrichedQuoteReport analysis={analysis} />
              ) : (
                /* Fallback: ancien affichage si le rapport n'est pas enrichi */
                <>
                  {/* Score Card with Visual Risk Indicator */}
                  <div className="glass-card p-6 md:p-8 text-center">
                    <p className="text-zinc-400 mb-4">Score du devis</p>
                    <div className="relative inline-block">
                      <div className={`text-5xl md:text-7xl font-heading font-bold ${getScoreColor(analysis.global_score).text}`}>
                        {analysis.global_score}
                        <span className="text-2xl md:text-3xl text-zinc-500">/100</span>
                      </div>
                      
                      {/* Large Visual Badge */}
                      <div className={`mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-2xl ${getScoreColor(analysis.global_score).bg}/20 border-2 ${
                        analysis.global_score >= 80 ? 'border-green-500/50' : 
                        analysis.global_score >= 60 ? 'border-orange-500/50' : 'border-red-500/50'
                      }`}>
                        {analysis.global_score >= 80 ? (
                          <CheckCircle className={`w-7 h-7 ${getScoreColor(analysis.global_score).text}`} />
                        ) : analysis.global_score >= 60 ? (
                          <AlertCircle className={`w-7 h-7 ${getScoreColor(analysis.global_score).text}`} />
                        ) : (
                          <AlertTriangle className={`w-7 h-7 ${getScoreColor(analysis.global_score).text}`} />
                        )}
                        <span className={`text-lg md:text-xl font-bold ${getScoreColor(analysis.global_score).text}`}>
                          {getScoreColor(analysis.global_score).label}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Léon explique (fallback) */}
                  <div className="glass-card p-5 md:p-6 border-gold-500/20">
                    <div className="flex items-start gap-3 md:gap-4">
                      <LeonAvatar className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0" />
                      <div>
                        <p className="text-gold-500 font-heading font-bold text-base md:text-lg mb-2">
                          Léon t'explique
                        </p>
                        <p className="text-sm md:text-base text-zinc-300 leading-relaxed">
                          {analysis.global_score >= 80 
                            ? "Ce devis est globalement bien construit ! Les mentions obligatoires sont présentes et les prix correspondent aux moyennes du marché."
                            : analysis.global_score >= 60
                              ? "Ce devis présente quelques points d'attention. Certains éléments doivent être clarifiés afin d'éviter des coûts supplémentaires."
                              : "Ce devis nécessite des clarifications importantes. Plusieurs éléments manquent ou sont imprécis."
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Lead Capture CTA - "Recevoir des devis d'artisans" */}
              <LeadCaptureCTA
                analysisType="quote"
                analysisId={analysis?.analysis_id}
                projectSummary={analysis?.executive_summary?.main_observation || file?.name || "Analyse de devis"}
              />

              {/* Disclaimer + Checkbox + Actions */}
              <div className="space-y-4">
                {/* Disclaimer */}
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-gold-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      <span className="text-zinc-300 font-medium">Avertissement :</span> Assistant by Léon fournit une analyse automatisée d'aide à la décision, à titre informatif et indicatif. Les résultats proposés ne constituent ni un diagnostic technique exhaustif, ni une mission de maîtrise d'œuvre, ni un contrôle de conformité sur site, ni un avis juridique. L'utilisateur reste seul responsable de ses décisions, signatures, paiements et choix d'entreprise.
                    </p>
                  </div>
                </div>

                {/* Checkbox for PDF download */}
                <label className="flex items-start gap-3 p-4 bg-zinc-800/30 rounded-xl border border-zinc-700 cursor-pointer hover:bg-zinc-800/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={disclaimerAccepted}
                    onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-gold-500 focus:ring-gold-500 focus:ring-offset-zinc-900"
                    data-testid="disclaimer-checkbox"
                  />
                  <span className="text-sm text-zinc-300 leading-relaxed">
                    Je reconnais que le rapport Assistant by Léon constitue une aide à la décision, non un diagnostic exhaustif ni un contrôle sur site, et qu'il ne remplace pas l'avis d'un professionnel qualifié.
                  </span>
                </label>

                {/* Action buttons */}
                <div className="flex flex-col gap-3 md:gap-4">
                  {/* Primary: Printable Report Button */}
                  <Button
                    onClick={() => {
                      if (disclaimerAccepted && analysis) {
                        window.open(`/report/${analysis.analysis_id}`, '_blank');
                      }
                    }}
                    disabled={!disclaimerAccepted}
                    className={`w-full py-6 md:py-7 font-bold text-base md:text-lg transition-all ${
                      disclaimerAccepted
                        ? 'bg-gold-500 hover:bg-gold-400 text-black' 
                        : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                    }`}
                    data-testid="open-printable-report-btn"
                  >
                    <FileText className="w-5 h-5 md:w-6 md:h-6 mr-2" />
                    Ouvrir le rapport imprimable
                  </Button>
                  
                  <p className="text-zinc-500 text-xs text-center -mt-2 mb-2">
                    Ouvre une version imprimable du rapport. Utilisez "Imprimer" puis "Enregistrer en PDF" pour sauvegarder.
                  </p>

                  {/* Secondary buttons row */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={() => { 
                        setAnalysis(null); 
                        setFile(null); 
                        setPreview(null); 
                        setDisclaimerAccepted(false);
                        if (id) navigate('/analyze');
                      }}
                      variant="outline"
                      className="flex-1 py-5 md:py-6 border-gold-500/30 text-gold-500 hover:bg-gold-500/10 text-sm md:text-base"
                      data-testid="analyze-new-btn"
                    >
                      <FileSearch className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                      Analyser un autre devis
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>

      {/* Email Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEmailModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-zinc-900 border border-gold-500/30 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-gold-500/20">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold-500/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-gold-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-white">
                      Email pour l'artisan
                    </h3>
                    <p className="text-zinc-500 text-sm">Prêt à être envoyé</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  data-testid="close-email-modal"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              {/* Email Content */}
              <div className="p-5 overflow-y-auto max-h-[50vh]">
                {/* Subject */}
                <div className="mb-4">
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">
                    Objet
                  </label>
                  <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <p className="text-gold-500 font-medium">
                      {generateArtisanEmail().subject}
                    </p>
                  </div>
                </div>

                {/* Body */}
                <div>
                  <label className="text-xs text-zinc-500 uppercase tracking-wider mb-1 block">
                    Message
                  </label>
                  <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <pre className="text-zinc-300 whitespace-pre-wrap font-sans text-sm leading-relaxed">
                      {generateArtisanEmail().body}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="p-5 border-t border-gold-500/20 bg-zinc-900/50">
                {/* Copy confirmation message */}
                {emailCopied && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <p className="text-green-500 font-medium">Email copié dans le presse-papiers</p>
                  </motion.div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleCopyEmail}
                    className={`flex-1 py-4 ${emailCopied 
                      ? 'bg-green-500 hover:bg-green-400 text-black' 
                      : 'bg-gold-500/10 hover:bg-gold-500/20 border border-gold-500/30 text-gold-500'
                    } font-bold transition-all`}
                    data-testid="copy-email-btn"
                  >
                    {emailCopied ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Copié !
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5 mr-2" />
                        Copier l'email
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleOpenMailClient}
                    className="flex-1 py-4 bg-gold-500 hover:bg-gold-400 text-black font-bold"
                    data-testid="open-mail-client-btn"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Ouvrir dans mon logiciel mail
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Duplicate Detection Modal */}
      <AnimatePresence>
        {showDuplicateModal && duplicateFound && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDuplicateModal(false)}
            data-testid="duplicate-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-zinc-900 border border-amber-500/30 rounded-2xl max-w-lg w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="duplicate-modal"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-5 border-b border-amber-500/20 bg-amber-500/5">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <History className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-heading font-bold text-white">
                      Devis déjà analysé
                    </h3>
                    <p className="text-amber-500/80 text-sm">Analyse existante détectée</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDuplicateModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  data-testid="close-duplicate-modal"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-5 space-y-4">
                {/* Info message */}
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-zinc-300 text-sm leading-relaxed">
                        Ce document a déjà été analysé. Vous pouvez consulter l'analyse existante ou lancer une nouvelle analyse si le devis a été modifié.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Existing analysis info */}
                {duplicateFound.existing_analysis && (
                  <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                    <div className="flex items-center gap-3 mb-3">
                      <FileText className="w-5 h-5 text-zinc-400" />
                      <span className="text-zinc-400 text-sm font-medium">Analyse précédente</span>
                    </div>
                    <div className="space-y-2">
                      <p className="text-white font-medium">
                        {duplicateFound.existing_analysis.filename || 'Devis analysé'}
                      </p>
                      {duplicateFound.existing_analysis.global_score && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-zinc-500">Score :</span>
                          <span className={`font-bold ${
                            duplicateFound.existing_analysis.global_score >= 80 ? 'text-green-500' :
                            duplicateFound.existing_analysis.global_score >= 60 ? 'text-orange-500' : 'text-red-500'
                          }`}>
                            {duplicateFound.existing_analysis.global_score}/100
                          </span>
                        </div>
                      )}
                      {duplicateFound.existing_analysis.created_at && (
                        <p className="text-xs text-zinc-500">
                          Analysé le {new Date(duplicateFound.existing_analysis.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                      {duplicateFound.existing_analysis.executive_summary && (
                        <p className="text-sm text-zinc-400 mt-2 italic">
                          "{duplicateFound.existing_analysis.executive_summary}"
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="p-5 border-t border-zinc-800 bg-zinc-900/50 space-y-3">
                <Button
                  onClick={handleViewExistingAnalysis}
                  className="w-full py-4 bg-gold-500 hover:bg-gold-400 text-black font-bold"
                  data-testid="view-existing-analysis-btn"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Voir l'analyse existante
                </Button>
                
                <Button
                  onClick={handleAnalyzeAnyway}
                  variant="outline"
                  className="w-full py-4 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                  disabled={analyzing}
                  data-testid="analyze-anyway-btn"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyse en cours...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2" />
                      Analyser quand même
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-zinc-500 text-center">
                  Une nouvelle analyse remplacera l'ancienne.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Tutorial Welcome Card */}
      <AnimatePresence>
        {showWelcome && !analysis && !loadingExisting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <TutorialWelcomeCard 
              config={tutorialConfig}
              onStart={startTutorial} 
              onDismiss={dismissWelcome}
              onDisableTutorials={disableAllTutorials}
            />
          </div>
        )}
      </AnimatePresence>
      
      {/* Leon Tutorial Modal */}
      <LeonTutorial 
        isOpen={showTutorial}
        onClose={closeTutorial}
        onComplete={completeTutorial}
        config={tutorialConfig}
      />

      {/* Video Ad Modal (before analysis for free users) */}
      <AdGateModal />

      {/* Lead Interest Popup (after analysis for free users) */}
      <LeadInterestPopup
        isOpen={showLeadPopup}
        onClose={() => setShowLeadPopup(false)}
        analysisType="quote"
        analysisId={analysis?.analysis_id}
        projectSummary={analysis?.executive_summary?.main_observation || file?.name || "Analyse de devis"}
        analysisContent={analysis?.executive_summary?.main_observation}
        detectedCategory={analysis?.work_type || null}
        onLeadSubmitted={() => {
          setLeadSubmitted(true);
          setShowLeadPopup(false);
        }}
      />

      {/* Sticky Banner Ad (bottom, for free users) */}
      {!isPremium && !analyzing && <BannerAd placement="bottom" sticky={true} />}
    </div>
  );
}
